import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  MapPin, Calendar, Clock, Users, Briefcase, Car, 
  CreditCard, AlertTriangle, CheckCircle, XCircle, 
  Loader2, ArrowLeft, Phone, Mail, FileText, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const ManageBookingPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [cancellation, setCancellation] = useState(null);
  const [error, setError] = useState(null);
  
  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid booking link');
      setLoading(false);
      return;
    }
    
    fetchBookingDetails();
  }, [token]);

  // Check for cancelled param from returning
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.success('Your booking has been cancelled successfully');
    }
  }, [searchParams]);

  const fetchBookingDetails = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/manage/validate/${token}`);
      
      if (response.status === 404) {
        setError('This booking link is invalid or has expired.');
        setLoading(false);
        return;
      }
      
      if (response.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setBooking(data.booking);
        setCancellation(data.cancellation);
      } else {
        setError(data.detail || 'Failed to load booking details');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Unable to load booking. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    setCancelling(true);
    setCancelResult(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/manage/cancel/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'customer_request' })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCancelResult({ success: true, message: data.message });
        toast.success('Booking cancelled successfully');
        // Refresh booking details
        setTimeout(() => {
          setShowCancelModal(false);
          fetchBookingDetails();
        }, 2000);
      } else {
        setCancelResult({ 
          success: false, 
          message: data.message || 'Cancellation failed'
        });
      }
    } catch (err) {
      setCancelResult({ 
        success: false, 
        message: 'Unable to process cancellation. Please try again.' 
      });
    } finally {
      setCancelling(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: booking?.currency || 'EUR' 
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Confirmed' },
      paid_confirmed: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Confirmed' },
      pending: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Pending' },
      pending_payment: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Awaiting Payment' },
      cancel_requested: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Cancellation Pending' },
      cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Cancelled' },
      cancellation_failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Active' },
      completed: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Completed' }
    };
    
    const style = styles[status] || styles.pending;
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-2xl mx-auto px-4 flex flex-col items-center justify-center min-h-[50vh]">
            <Loader2 className="h-12 w-12 animate-spin text-[#FF8C00] mb-4" />
            <p className="text-gray-400">Loading your booking...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-2xl mx-auto px-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8 text-center">
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Link Invalid</h1>
                <p className="text-gray-400 mb-6">{error}</p>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-[#FF8C00] hover:bg-[#E67E00]"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Main content
  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Manage Your Booking</h1>
            <p className="text-gray-400">
              Reference: <span className="text-[#FF8C00] font-mono">{booking?.booking_ref}</span>
            </p>
          </div>

          {/* Status Card */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Booking Status</p>
                  {getStatusBadge(booking?.booking_status || booking?.status)}
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-400 mb-1">Total Amount</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">
                    {formatCurrency(booking?.total)}
                  </p>
                </div>
              </div>
              
              {booking?.cancelled_at && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">
                    Cancelled on {new Date(booking.cancelled_at).toLocaleDateString()}
                    {booking.cancel_reason && ` • Reason: ${booking.cancel_reason}`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transfer Details */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#FF8C00]" />
                Transfer Details
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5" />
                  <div>
                    <p className="text-xs text-gray-400">Pickup</p>
                    <p className="text-white">{booking?.pickup_location}</p>
                  </div>
                </div>
                
                <div className="ml-1.5 w-0.5 h-6 bg-zinc-700" />
                
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5" />
                  <div>
                    <p className="text-xs text-gray-400">Dropoff</p>
                    <p className="text-white">{booking?.dropoff_location}</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-zinc-800">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400">Date</p>
                    <p className="text-white">{booking?.trip_date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400">Time</p>
                    <p className="text-white">{booking?.trip_time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400">Passengers</p>
                    <p className="text-white">{booking?.passengers}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Briefcase className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400">Luggage</p>
                    <p className="text-white">{booking?.luggage}</p>
                  </div>
                </div>
              </div>
              
              {booking?.vehicle_name && (
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-zinc-800">
                  <Car className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400">Vehicle</p>
                    <p className="text-white">{booking.vehicle_name}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Info */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-[#FF8C00]" />
                Payment Information
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400">Method</p>
                  <p className="text-white uppercase">{booking?.payment_method || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Status</p>
                  <p className="text-white capitalize">{booking?.payment_status || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cancellation Section */}
          {cancellation && (
            <Card className="bg-zinc-900 border-zinc-800 mb-6">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#FF8C00]" />
                  Cancellation
                </h2>
                
                {cancellation.can_cancel ? (
                  <>
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                        <div>
                          <p className="text-green-400 font-medium">Cancellation Available</p>
                          <p className="text-sm text-green-400/80">{cancellation.message}</p>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      variant="destructive"
                      onClick={() => setShowCancelModal(true)}
                      className="w-full"
                      data-testid="cancel-booking-btn"
                    >
                      Cancel Booking
                    </Button>
                  </>
                ) : (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="text-red-400 font-medium">Cannot Cancel</p>
                        <p className="text-sm text-red-400/80">{cancellation.message}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <a 
                  href={cancellation.policy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#FF8C00] hover:underline mt-4 inline-block"
                >
                  View Cancellation Policy →
                </a>
              </CardContent>
            </Card>
          )}

          {/* Contact Support */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Info className="h-5 w-5 text-[#FF8C00]" />
                Need Help?
              </h2>
              
              <p className="text-gray-400 mb-4">
                Contact our support team for any questions or special requests.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <a 
                  href="mailto:info@goturkeytransfer.com"
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg text-white hover:bg-zinc-700 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  info@goturkeytransfer.com
                </a>
                <a 
                  href="tel:+905352385797"
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg text-white hover:bg-zinc-700 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  +90 535 238 5797
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-zinc-900 border-zinc-800 w-full max-w-md">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">Cancel Booking?</h2>
                <p className="text-gray-400">
                  Are you sure you want to cancel this booking? This action cannot be undone.
                </p>
              </div>
              
              <div className="p-3 bg-zinc-800 rounded-lg mb-6">
                <p className="text-sm text-gray-300">
                  <strong>Ref:</strong> {booking?.booking_ref}<br />
                  <strong>Date:</strong> {booking?.trip_date} at {booking?.trip_time}
                </p>
              </div>
              
              {cancelResult && (
                <div className={`p-4 rounded-lg mb-4 ${
                  cancelResult.success 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <div className="flex items-center gap-2">
                    {cancelResult.success ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <p className={cancelResult.success ? 'text-green-400' : 'text-red-400'}>
                      {cancelResult.message}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancelling}
                  className="flex-1 border-zinc-700 text-white hover:bg-zinc-800"
                >
                  Go Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleCancelBooking}
                  disabled={cancelling || cancelResult?.success}
                  className="flex-1"
                  data-testid="confirm-cancel-btn"
                >
                  {cancelling ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    'Yes, Cancel Booking'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ManageBookingPage;
